import { Box, Button, Grid, makeStyles, Typography } from '@material-ui/core';
import { hammer } from 'ionicons/icons';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { EarningsCards } from '../../components/earningsCards';
import { StepTitle } from '../../components/stepTitle';
import { QuoteTypeStore } from '../../stores/quoteTypeStore';
import { LoanDetailsCardProps } from '../summary/summaryStep';
import { EconomicsCard } from './components/economicsCard';
import { CorrespondentBetaCard } from './components/correspondentBetaCard';
import { useEarningsCardSticky } from '../../hooks/useEarningsCardSticky';
import { useStore } from '@roc/client-portal-shared/hooks';
import { isFixAndFlip, isGroundUp } from '@roc/feature-utils';
import { useUserStore } from '@roc/feature-app-core';
import { InternalValuationPilotDialog } from '../leverage/components/internalValuationPilotDialog';

const useStyles = makeStyles(theme => ({
  ivQuestionContainer: {
    border: '1px solid #326bbd',
    backgroundColor: '#f0f9ff',
    minWidth: '100%',
    borderRadius: '10px'
  },
  button: {
    marginTop: '25px',
    marginBottom: '25px',
    width: '200px'
  },
  text: {
    marginTop: '10px',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  }
}));

interface EconomicsStepProps {
  store: QuoteTypeStore;
  LoanDetailsCard: React.FC<LoanDetailsCardProps>;
  isManualQuote?: boolean;
}

export const EconomicsStep = observer(
  ({ store, LoanDetailsCard, isManualQuote = false }: EconomicsStepProps) => {
    const { earningsCardsStickyStyle } = useEarningsCardSticky();
    const { loanEconomicsStore, brokerStore } = store;
    const { loanEconomicsFormStore } = loanEconomicsStore;
    const { brokerFormStore } = brokerStore;
    const { globalStore } = useStore();
    const { userStore } = useUserStore();
    const loanData = store.getQuoteData();
    const classes = useStyles();
    const calculateSoftsCostsOnly = () => {
      for (const property of loanData?.properties || []) {
        if (property?.softCostsOnly === false) {
          return false;
        }
      }
      return true;
    }
    const openIVPilotDialog = () => {
      store.internalValuationPilotSelected = true;
      store.variantYesButton = 'contained'
      store.variantNoButton = 'outlined'
      store.internalValuationPilotDialogOpen = true;
    }
    const selectNO = () => {
      store.variantYesButton = 'outlined'
      store.variantNoButton = 'contained'
      if (!store.isEditingLoan) {
        store.saveQuote();
      }
      store.internalValuationPilotSelected = false;
    }
    useEffect(() => {
      if (isGroundUp(loanData?.loanSubtype)) {
        const ltc = loanEconomicsStore.enableManualQuote ?
          loanEconomicsStore.manualExceptionQuoteFormStore?.calculatedFields?.calculatedLTFC :
          loanEconomicsStore.instaLeverageQuoteFormStore?.calculatedFields?.calculatedLTFC;
        const calculatedFields = loanEconomicsStore.caclulateFields(
          store.propertiesStore.getProperties(),
          loanEconomicsStore.instaLeverageQuoteFormStore.getFormValues()
        );
        store.loanEconomicsStore.getRateFloor({
          lenderName: '',
          submissionDate: new Date().toJSON(),
          leverage: ltc,
          purchasePrice: loanData?.properties[0]?.purchasePrice || 0,
          permitsInPlace: hasPermitsInPlace(loanData?.properties),
          constructionCost: loanData?.properties[0]?.renovationBudget || 0,
          interestExpense: calculatedFields?.interestExpense,
          arv: loanData?.properties[0]?.marketValueAfterCompletion || 0,
          propertyState: loanData?.properties[0]?.state,
          enableFinancedIr: isGroundUp(loanData?.loanSubtype),
          loanSubtype: loanData?.loanSubtype,
          softCostsOnly: calculateSoftsCostsOnly(),
          lenderId: globalStore.lenderInfo?.lenderId,
          sellAs: store.correspondentSellAs
        });
        store.loadInternalValuationFields()
      } else if (isFixAndFlip(loanData?.loanSubtype)) {
        const isPropertyAndBorrowerHighBoxCriteriaMet = loanEconomicsStore.showHighLeverageBoxPricingAdjustments;
        const ltc = loanEconomicsStore.enableManualQuote ?
          loanEconomicsStore.manualExceptionQuoteFormStore?.form.fields.loanToCost.value :
          loanEconomicsStore.instaLeverageQuoteFormStore?.form.fields.loanToCost.value;
        const calculatedFields = loanEconomicsStore.caclulateFields(
          store.propertiesStore.getProperties(),
          loanEconomicsStore.instaLeverageQuoteFormStore.getFormValues()
        );
        store.loanEconomicsStore.getRateFloor({
          lenderName: '',
          submissionDate: new Date().toJSON(),
          leverage: ltc,
          purchasePrice: loanData?.properties[0]?.purchasePrice || 0,
          permitsInPlace: hasPermitsInPlace(loanData?.properties),
          constructionCost: loanData?.properties[0]?.renovationBudget || 0,
          interestExpense: calculatedFields?.interestExpense,
          arv: loanData?.properties[0]?.marketValueAfterCompletion || 0,
          propertyState: loanData?.properties[0]?.state,
          enableFinancedIr: isGroundUp(loanData?.loanSubtype),
          loanSubtype: loanData?.loanSubtype,
          isHighLeverageBox: loanEconomicsStore.meetsAllHighFicoCriteria,
          lenderId: globalStore.lenderInfo?.lenderId,
          sellAs: store.correspondentSellAs
        });
        store.loadInternalValuationFields()
      } else {
        store.loanEconomicsStore.fetchFloorRate();
      }
      store.loanEconomicsStore.fetchBorrowerPointsOptions();
    }, [
      store.quoteId,
      loanEconomicsFormStore.form.fields.loanTermMonths.value,
      store.correspondentSellAs,
    ]);

    const hasPermitsInPlace = properties => {
      return properties.length === 1 && properties[0]?.permitsInPlace;
    };

    const lenderFees = loanEconomicsStore.feeStores.map(formStore => {
      return Object.keys(formStore.form.fields).map(
        field => formStore.form.fields[field].value
      );
    });

    const fields = loanEconomicsFormStore.form.fields;
    const brokertFields = brokerFormStore.form.fields;
    const hash = JSON.stringify(
      Object.keys(fields)
        .filter(
          fieldName =>
            !['capitalProviderPoints', 'lenderPoints'].includes(fieldName)
        )
        .map(fieldName => fields[fieldName].value)
    );
    const brokerHash = JSON.stringify(
      Object.keys(brokertFields).map(
        fieldName => brokertFields[fieldName].value
      )
    );
    const isWholeLoanParticipation = store.isWholeLoanParticipation;
    useEffect(() => {
      loanEconomicsStore.fetchQuoteOutputsDebounced();
    }, [hash, brokerHash, lenderFees, isWholeLoanParticipation]);

    return (
      <>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <StepTitle
              title={<>Loan Economics</>}
              subtitle={
                <>
                  Set the rate and fees that your borrower(s) will be charged
                  below. As the rate increases, so will your estimated future
                  earnings through monthly yield spread.
                </>
              }
            />
          </Grid>
          {userStore.allowLoanPricing && (
            <Grid item xs={12} style={earningsCardsStickyStyle}>
              <EarningsCards store={store} />
            </Grid>
          )}
          <Grid item xs={12}>
            <LoanDetailsCard store={store} isManualQuote={isManualQuote} />
          </Grid>
          {store.isCorrespondentBetaFormEnabled && (
            <Grid item xs={12}>
              <CorrespondentBetaCard store={store} />
            </Grid>
          )}
          {store.internalValuationPilotApplicable && !store.isEditingLoan && <Grid item container xs={12}>
            <Box className={classes.ivQuestionContainer}>
              <Grid item container xs={12} justifyContent='center' alignItems='center' className={classes.text}>
                <Typography variant='h6'>
                  Eligible for RealVal
                </Typography>
              </Grid>
              <Grid item container xs={12} justifyContent='center' alignItems='center' className={classes.text}>
                <Typography variant='body2'>
                  Based on your submission, this loan qualifies for RealVal — our in-house valuation program that delivers fast, reliable values to help get your loans approved quicker. Would you like to participate?
                </Typography>
              </Grid>
              <Grid item container xs={12} justifyContent='center' alignItems='center' direction='row' spacing={2}>
                <Grid item container xs={2} justifyContent='center'>
                  <Button variant={store.variantYesButton} color='primary' className={classes.button} onClick={() => openIVPilotDialog()}>
                    YES
                  </Button>
                </Grid>
                <Grid item container xs={2} justifyContent='center'>
                  <Button variant={store.variantNoButton} color='primary' className={classes.button} onClick={() => selectNO()}>
                    NO
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Grid>}
          <Grid item xs={12}>
            <EconomicsCard store={store} />
          </Grid>
        </Grid>
        <InternalValuationPilotDialog store={store} />
      </>
    );
  }
);
